import { Modal, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { toggleCustomChartModal } from '../../../../../actions/modalAction';
import { getPortfolios } from '../../../../../actions/WorkspaceActions';
import {
  DASHBOARD_CHART_COLORS,
  NIVO_COLORS,
  REPORTS,
} from '../../../../../js/constants';
import { getGroupLabel } from '../../../../../js/helper';
import { createDashboard } from '../../../../../redux/state-areas/dashboards/actions';
import {
  aggregateQuery,
  createReport,
  updateReport,
} from '../../../../../redux/state-areas/reports/actions';
import { getReportById } from '../../../../../redux/state-areas/reports/entities/selectors';
import { getWorkspaceCustomFields } from '../../../../../redux/workspace/selectors';
import Label from '../../../atoms/label/Label';
import LabeledDropdown from '../../../atoms/labeled-dropdown/LabeledDropdown';
import LabeledInput from '../../../atoms/labeled-input/LabeledInput';
import LabeledSelect from '../../../atoms/labeled-select/LabeledSelect';
import FilterDropdown from '../../upper-navbar/FilterDropdown';
import ChartWrapper from '../charts/ChartWrapper';
import {
  CHART_TYPE_DEFINITIONS,
  CHART_TYPES,
  SUPPORTED_CHART_TYPE_DEFINITIONS,
} from '../charts/constants';

import {
  EXCLUDED_CHART_TYPES_FOR_X_AXIS,
  GROUP_OPTIONS,
  INCLUDE_FROM_PORTFOLIOS,
  INCLUDE_FROM_PROJECTS,
  INTERVAL_OPTIONS,
  RANGE_TYPE_OPTIONS,
  REPORT_ON_KEY,
  TIME_UNIT_OPTIONS,
} from './chartCreationConstants';
import { toQueryString } from './chartCreationUtils';
import './style.css';

const MODAL_STYLES = {
  body: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    paddingLeft: '30px',
    paddingBottom: '20px',
    maxHeight: '70vh',
    overflowY: 'hidden',
    overflowX: 'auto',
  },
  content: {
    maxWidth: '1250px',
  },
  header: {
    padding: '10px 30px',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  wrapper: {
    overflow: 'hidden',
  },
};

const MODAL_WIDTH = '99vw';
const MODAL_MINWIDTH = { maxWidth: '1250px' };

const FOOTER_STYLE = { opacity: 0.5 };
const FOOTER_STYLE_ENABLED = { opacity: 1 };

const CUSTOM_FIELD_ARROW_STYLE = {
  height: '10px',
  width: '10px',
  margin: '0 10px 0 5px',
};

const CustomFieldArrow = () => (
  <img
    src="/images/icons/black-arrow-down.svg"
    className="transition-transform duration-300"
    style={CUSTOM_FIELD_ARROW_STYLE}
    alt="icon"
  />
);

const CustomFieldLabel = () => (
  <div className="flex align-center justify-between">
    <div>
      <FormattedMessage id="CUSTOM_FIELD" defaultMessage="Custom Field" />
    </div>
    <CustomFieldArrow />
  </div>
);

const ChartIcon = ({ chartType }) => {
  const Icon = CHART_TYPE_DEFINITIONS[chartType]?.icon;
  return (
    <div className="flex items-center">
      <span className="mr-2 text-gray-400">{Icon && <Icon />}</span>
      <span>{CHART_TYPE_DEFINITIONS[chartType]?.label}</span>
    </div>
  );
};

const ModalFooter = ({ disabled, isEdit, onConfirmBtnClick }) => (
  <div
    className="pb-3 px-6"
    style={disabled ? FOOTER_STYLE : FOOTER_STYLE_ENABLED}
  >
    <button
      onClick={onConfirmBtnClick}
      disabled={disabled}
      className="p-1.5 bg-blue-500 px-3 rounded text-white cursor-pointer hover:opacity-60 disabled:cursor-not-allowed"
    >
      {isEdit ? (
        <FormattedMessage id="UPDATE" defaultMessage="Update" />
      ) : (
        <FormattedMessage id="CREATE" defaultMessage="Create" />
      )}
    </button>
  </div>
);

const ChartPreview = ({
  chartData,
  currentGroupValue,
  groupValue,
  isStackedOrGrouped,
  measure,
  onReportTitleChange,
  reportTitle,
  selectedChart,
  workspaceCustomFields,
}) => {
  const chartTitle = useMemo(() => {
    if (selectedChart === CHART_TYPES.BURNUP) {
      return 'Tasks completion over time';
    }
    if (selectedChart === CHART_TYPES.BURNDOWN) {
      return 'Remaining tasks over time';
    }
    if (selectedChart === CHART_TYPES.NUMBER) {
      return 'Total tasks';
    }
    if (isStackedOrGrouped && groupValue) {
      const groupByLabel = getGroupLabel(
        currentGroupValue,
        workspaceCustomFields,
      );
      const stackByLabel = getGroupLabel(groupValue, workspaceCustomFields);
      return `Total tasks by ${groupByLabel} and ${stackByLabel}`;
    }
    return `Total tasks by ${getGroupLabel(currentGroupValue, workspaceCustomFields)}`;
  }, [
    selectedChart,
    isStackedOrGrouped,
    groupValue,
    currentGroupValue,
    workspaceCustomFields,
  ]);

  return (
    <div className="grow shrink-0 basis-[65%] h-full">
      <div className="w-full p-3 h-full px-3 flex flex-col">
        <input
          type="text"
          value={reportTitle}
          onChange={(e) => onReportTitleChange(e.target.value)}
          placeholder={chartTitle}
          className="chart-title-input text-xl font-medium mt-4 pl-2 pt-1 pb-1 pr-3 text-gray-800 rounded-md border border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-400 placeholder-gray-700"
        />
        <div className="w-full flex h-full items-center justify-center">
          <ChartWrapper
            chartType={selectedChart}
            data={chartData}
            measure={measure}
          />
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ defaultMessage, messageId }) => (
  <div className="custom-chart-modal-section-label">
    <FormattedMessage id={messageId} defaultMessage={defaultMessage} />
  </div>
);

const HorizontalSeparator = () => (
  <div className="custom-chart-modal-horizontal-separator" />
);

const FormattedLabel = ({ id, defaultMessage }) => (
  <FormattedMessage
    id={`${id}`.toUpperCase().replace('-', '_')}
    defaultMessage={defaultMessage}
  />
);

const resolveIncludeFrom = (query) => {
  if (query?.projectIds) return INCLUDE_FROM_PROJECTS;
  if (query?.portfolioIds) return INCLUDE_FROM_PORTFOLIOS;
  return '';
};

const parseBurnRange = (rangeString) => {
  if (!rangeString) return {};

  if (rangeString.startsWith('last:')) {
    const [, number, unit] = rangeString.split(':');
    return {
      rangeType: RANGE_TYPE_OPTIONS.LAST,
      rangeNumber: number,
      timeUnit: unit,
    };
  }

  if (rangeString.startsWith('between:')) {
    const [start, end] = rangeString.replace('between:', '').split(',');
    return {
      rangeType: RANGE_TYPE_OPTIONS.BETWEEN,
      startDate: dayjs(start),
      endDate: dayjs(end),
    };
  }

  return {};
};

const parseFiltersFromQuery = (query) => {
  if (!query) return {};
  const {
    burndown,
    burnup,
    creator,
    groupBy,
    interval,
    portfolioIds,
    projectIds,
    reportOn,
    stackBy,
    ...rest
  } = query;

  const filters = { ...rest };

  return Object.keys(filters)?.reduce((result, currentFilterKey) => {
    const customFieldMatch = currentFilterKey.match(/^customField\[(.+)\]$/);
    const resolvedKey = customFieldMatch ? customFieldMatch[1] : currentFilterKey;

    return {
      ...result,
      [resolvedKey]: filters?.[currentFilterKey]?.split(',').filter(Boolean),
    };
  }, {});
};

const DEFAULT_OBJECT = {};
const PREVIEW_DEBOUNCE_MS = 600;

const CustomChartModal = ({
  currentReport,
  currentWorkspace,
  data,
  isEdit,
  isModalOpen,
  projects,
  workspaceCustomFields,
  workspacePortfolios,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const previewSequenceRef = useRef(0);

  const [dashboardTitle, setDashboardTitle] = useState(
    data?.dashboardTitle || '',
  );
  const [reportTitle, setReportTitle] = useState('');
  const [includeFrom, setIncludeFrom] = useState('');
  const [xAxisValue, setXAxisValue] = useState('');
  const [groupValue, setGroupValue] = useState('');
  const [measure, setMeasure] = useState(REPORTS.MEASURES.TASK_COUNT);
  const [options, setOptions] = useState([]);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_OBJECT);
  const [loadedFilters, setLoadedFilters] = useState(DEFAULT_OBJECT);
  const [selectedColor, setSelectedColor] = useState(DASHBOARD_CHART_COLORS[0]);
  const [selectedChart, setSelectedChart] = useState(
    SUPPORTED_CHART_TYPE_DEFINITIONS[0].key,
  );
  const [chartData, setChartData] = useState({});
  const [interval, setInterval] = useState(INTERVAL_OPTIONS.DAY);
  const [rangeType, setRangeType] = useState(RANGE_TYPE_OPTIONS.LAST);
  const [rangeNumber, setRangeNumber] = useState('30');
  const [timeUnit, setTimeUnit] = useState(TIME_UNIT_OPTIONS.DAYS);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [editableFieldsLoaded, setEditableFieldsLoaded] = useState(false);

  useEffect(() => {
    if (!isEdit || editableFieldsLoaded) return;

    const { title, type, query, measure: savedMeasure } = currentReport;

    setReportTitle(title || '');
    setSelectedChart(type || SUPPORTED_CHART_TYPE_DEFINITIONS[0].key);
    setMeasure(savedMeasure || REPORTS.MEASURES.TASK_COUNT);

    if (!query) {
      setEditableFieldsLoaded(true);
      return;
    }

    const from = resolveIncludeFrom(query);
    setIncludeFrom(from);

    if (from === INCLUDE_FROM_PROJECTS) {
      const ids = query.projectIds?.split(',').filter(Boolean) || [];
      const matched = (projects || []).filter((p) => ids.includes(p._id));
      setPortfolioProjects(matched);
      setOptions(
        (projects || []).map((p) => ({ value: p._id, label: p.title })),
      );
    } else if (from === INCLUDE_FROM_PORTFOLIOS) {
      const ids = query.portfolioIds?.split(',').filter(Boolean) || [];
      const matched = (workspacePortfolios || []).filter((p) =>
        ids.includes(p._id),
      );
      setPortfolioProjects(matched);
      setOptions(
        (workspacePortfolios || []).map((p) => ({
          value: p._id,
          label: p.name,
        })),
      );
    }

    const isBurn = query.groupBy === GROUP_OPTIONS.BURN_REPORT;
    if (!isBurn) {
      const isStackedGrouped =
        type === CHART_TYPES.STACKED_BAR || type === CHART_TYPES.GROUPED_BAR;
      const isPie = type === CHART_TYPES.PIE;

      if (isPie) {
        setGroupValue(query.groupBy || '');
      } else if (isStackedGrouped) {
        setXAxisValue(query.groupBy || '');
        setGroupValue(query.stackBy || '');
      } else {
        setXAxisValue(query.groupBy || '');
      }
    }

    if (isBurn) {
      setInterval(query.interval || INTERVAL_OPTIONS.DAY);
      const rangeString = query.burnup || query.burndown || '';
      const parsed = parseBurnRange(rangeString);
      if (parsed.rangeType) setRangeType(parsed.rangeType);
      if (parsed.rangeNumber) setRangeNumber(parsed.rangeNumber);
      if (parsed.timeUnit) setTimeUnit(parsed.timeUnit);
      if (parsed.startDate) setStartDate(parsed.startDate);
      if (parsed.endDate) setEndDate(parsed.endDate);
    }

    setLoadedFilters(parseFiltersFromQuery(query));
  }, [
    currentReport,
    editableFieldsLoaded,
    isEdit,
    projects,
    workspacePortfolios,
  ]);

  useEffect(() => {
    if (!isEdit || (!isEmpty(loadedFilters) && isEmpty(filters))) {
      return;
    }
    setEditableFieldsLoaded(true);
  }, [isEdit, filters, loadedFilters]);

  useEffect(() => {
    if (!isModalOpen) {
      setEditableFieldsLoaded(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    setDashboardTitle(data?.dashboardTitle || '');

    if (!isEdit || !isModalOpen) {
      setReportTitle('');
      setIncludeFrom('');
      setXAxisValue('');
      setGroupValue('');
      setMeasure(REPORTS.MEASURES.TASK_COUNT);
      setOptions([]);
      setPortfolioProjects([]);
      setFilters({});
      setLoadedFilters({});
      setSelectedColor(DASHBOARD_CHART_COLORS[0]);
      setSelectedChart(SUPPORTED_CHART_TYPE_DEFINITIONS[0].key);
      setChartData({});
      setInterval(INTERVAL_OPTIONS.DAY);
      setRangeType(RANGE_TYPE_OPTIONS.LAST);
      setRangeNumber('30');
      setTimeUnit(TIME_UNIT_OPTIONS.DAYS);
      setStartDate(null);
      setEndDate(null);
    }
  }, [data, isEdit, isModalOpen]);

  const closeModal = useCallback(() => {
    dispatch(toggleCustomChartModal(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isBurnChart = useMemo(
    () => [CHART_TYPES.BURNDOWN, CHART_TYPES.BURNUP].includes(selectedChart),
    [selectedChart],
  );

  const isStackedOrGrouped = useMemo(
    () =>
      selectedChart === CHART_TYPES.STACKED_BAR ||
      selectedChart === CHART_TYPES.GROUPED_BAR,
    [selectedChart],
  );

  const currentGroupValue = useMemo(() => {
    if (selectedChart === CHART_TYPES.PIE) return groupValue;
    if (isBurnChart) return null;
    return xAxisValue;
  }, [groupValue, isBurnChart, selectedChart, xAxisValue]);

  const stackByQuery = useMemo(
    () => (isStackedOrGrouped && groupValue ? `&stackBy=${groupValue}` : ''),
    [isStackedOrGrouped, groupValue],
  );

  const burnChartQuery = useMemo(() => {
    if (!isBurnChart) return '';

    let query = `&interval=${interval}`;

    if (rangeType === RANGE_TYPE_OPTIONS.LAST) {
      const chartType =
        selectedChart === CHART_TYPES.BURNUP ? 'burnup' : 'burndown';
      query += `&${chartType}=last:${rangeNumber}:${timeUnit}`;
    } else if (rangeType === RANGE_TYPE_OPTIONS.BETWEEN) {
      if (startDate && endDate) {
        const chartType =
          selectedChart === CHART_TYPES.BURNUP ? 'burnup' : 'burndown';
        const start = dayjs(startDate).format('YYYY-MM-DD');
        const end = dayjs(endDate).format('YYYY-MM-DD');
        query += `&${chartType}=between:${start},${end}`;
      }
    }

    return query;
  }, [
    endDate,
    interval,
    isBurnChart,
    rangeNumber,
    rangeType,
    selectedChart,
    startDate,
    timeUnit,
  ]);

  const isBurnChartDataSelected = useMemo(() => {
    if (!isBurnChart) return true;
    if (!interval) return false;
    if (rangeType === RANGE_TYPE_OPTIONS.LAST) return rangeNumber && timeUnit;
    if (rangeType === RANGE_TYPE_OPTIONS.BETWEEN) return startDate && endDate;
    return false;
  }, [
    endDate,
    interval,
    isBurnChart,
    rangeNumber,
    rangeType,
    startDate,
    timeUnit,
  ]);

  const disabled = useMemo(() => {
    if (portfolioProjects?.length === 0 || !includeFrom || !measure) {
      return true;
    }

    if (!isEdit && !data?.dashboardId && !dashboardTitle) {
      return true;
    }

    if (isBurnChart) return !isBurnChartDataSelected;
    if (selectedChart === CHART_TYPES.PIE) return !groupValue;
    if (isStackedOrGrouped) return !xAxisValue || !groupValue;
    return !xAxisValue;
  }, [
    dashboardTitle,
    data?.dashboardId,
    groupValue,
    includeFrom,
    isBurnChart,
    isBurnChartDataSelected,
    isEdit,
    isStackedOrGrouped,
    measure,
    portfolioProjects,
    selectedChart,
    xAxisValue,
  ]);

  const reportOnItems = useMemo(
    () => [
      {
        key: REPORT_ON_KEY,
        label: (
          <REPORT_ON_KEY id={INCLUDE_FROM_PORTFOLIOS} defaultMessage="Tasks" />
        ),
        onClick: () => {},
      },
    ],
    [],
  );

  const includeTasksFromItems = useMemo(
    () => [
      {
        key: INCLUDE_FROM_PORTFOLIOS,
        label: (
          <FormattedLabel
            id={INCLUDE_FROM_PORTFOLIOS}
            defaultMessage="Portfolios"
          />
        ),
        onClick: () => {
          setIncludeFrom(INCLUDE_FROM_PORTFOLIOS);
          setOptions(
            workspacePortfolios?.map((port) => ({
              value: port?._id,
              label: port?.name,
            })),
          );
          setPortfolioProjects([]);
        },
      },
      {
        key: INCLUDE_FROM_PROJECTS,
        label: (
          <FormattedLabel
            id={INCLUDE_FROM_PROJECTS}
            defaultMessage="Projects"
          />
        ),
        onClick: () => {
          setIncludeFrom(INCLUDE_FROM_PROJECTS);
          setOptions(
            projects?.map((project) => ({
              value: project?._id,
              label: project?.title,
            })),
          );
          setPortfolioProjects([]);
        },
      },
    ],
    [projects, workspacePortfolios],
  );

  const selectChartType = useCallback((chartType) => {
    setChartData({});
    setSelectedChart(chartType);
    if (chartType === CHART_TYPES.NUMBER) {
      setXAxisValue(GROUP_OPTIONS.COMPLETION);
    }
  }, []);

  const chartStyleItems = useMemo(
    () =>
      SUPPORTED_CHART_TYPE_DEFINITIONS.map((chartType) => {
        const Icon = chartType?.icon;
        return {
          ...chartType,
          icon: <span className="mr-2 text-gray-400">{Icon && <Icon />}</span>,
          onClick: () => selectChartType(chartType.key),
        };
      }),
    [selectChartType],
  );

  const intervalItems = useMemo(
    () => [
      {
        key: INTERVAL_OPTIONS.DAY,
        label: (
          <FormattedLabel id={`CAPITALIZED_${INTERVAL_OPTIONS.DAY}`} defaultMessage="Day" />
        ),
        onClick: () => setInterval(INTERVAL_OPTIONS.DAY),
      },
      {
        key: INTERVAL_OPTIONS.WEEK,
        label: (
          <FormattedLabel id={`CAPITALIZED_${INTERVAL_OPTIONS.WEEK}`} defaultMessage="Week" />
        ),
        onClick: () => setInterval(INTERVAL_OPTIONS.WEEK),
      },
      {
        key: INTERVAL_OPTIONS.MONTH,
        label: (
          <FormattedLabel id={`CAPITALIZED_${INTERVAL_OPTIONS.MONTH}`} defaultMessage="Month" />
        ),
        onClick: () => setInterval(INTERVAL_OPTIONS.MONTH),
      },
      {
        key: INTERVAL_OPTIONS.QUARTER,
        label: (
          <FormattedLabel
            id={`CAPITALIZED_${INTERVAL_OPTIONS.QUARTER}`}
            defaultMessage="Quarter"
          />
        ),
        onClick: () => setInterval(INTERVAL_OPTIONS.QUARTER),
      },
      {
        key: INTERVAL_OPTIONS.YEAR,
        label: (
          <FormattedLabel id={`CAPITALIZED_${INTERVAL_OPTIONS.YEAR}`} defaultMessage="Year" />
        ),
        onClick: () => setInterval(INTERVAL_OPTIONS.YEAR),
      },
    ],
    [],
  );

  const rangeTypeItems = useMemo(
    () => [
      {
        key: RANGE_TYPE_OPTIONS.LAST,
        label: (
          <FormattedLabel
            id={RANGE_TYPE_OPTIONS.LAST}
            defaultMessage="Within the last"
          />
        ),
        onClick: () => setRangeType(RANGE_TYPE_OPTIONS.LAST),
      },
      {
        key: RANGE_TYPE_OPTIONS.BETWEEN,
        label: (
          <FormattedLabel
            id={RANGE_TYPE_OPTIONS.BETWEEN}
            defaultMessage="Between"
          />
        ),
        onClick: () => setRangeType(RANGE_TYPE_OPTIONS.BETWEEN),
      },
    ],
    [],
  );

  const timeUnitItems = useMemo(
    () => [
      {
        key: TIME_UNIT_OPTIONS.DAYS,
        label: (
          <FormattedLabel id={TIME_UNIT_OPTIONS.DAYS} defaultMessage="Days" />
        ),
        onClick: () => setTimeUnit(TIME_UNIT_OPTIONS.DAYS),
      },
      {
        key: TIME_UNIT_OPTIONS.WEEKS,
        label: (
          <FormattedLabel id={TIME_UNIT_OPTIONS.WEEKS} defaultMessage="Weeks" />
        ),
        onClick: () => setTimeUnit(TIME_UNIT_OPTIONS.WEEKS),
      },
      {
        key: TIME_UNIT_OPTIONS.MONTHS,
        label: (
          <FormattedLabel
            id={TIME_UNIT_OPTIONS.MONTHS}
            defaultMessage="Months"
          />
        ),
        onClick: () => setTimeUnit(TIME_UNIT_OPTIONS.MONTHS),
      },
    ],
    [],
  );

  const createCustomFieldItems = useCallback(
    (onClickHandler) =>
      workspaceCustomFields
        ?.filter((cf) => cf?.type === 'dropdown' || cf?.type === 'assignee')
        ?.map((cf) => ({
          key: `custom-field-${cf?._id}`,
          label: cf?.name,
          onClick: () => onClickHandler(cf),
        })),
    [workspaceCustomFields],
  );

  const groupItems = useMemo(
    () => [
      {
        key: GROUP_OPTIONS.ASSIGNEE,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.ASSIGNEE}
            defaultMessage="Assignee"
          />
        ),
        onClick: () => setGroupValue(GROUP_OPTIONS.ASSIGNEE),
      },
      {
        key: GROUP_OPTIONS.CREATOR,
        label: (
          <FormattedLabel id={GROUP_OPTIONS.CREATOR} defaultMessage="Creator" />
        ),
        onClick: () => setGroupValue(GROUP_OPTIONS.CREATOR),
      },
      {
        key: GROUP_OPTIONS.CUSTOM_FIELD,
        label: <CustomFieldLabel />,
        children: createCustomFieldItems((cf) =>
          setGroupValue(`${GROUP_OPTIONS.CUSTOM_FIELD_PREFIX}${cf?._id}`),
        ),
      },
      {
        key: GROUP_OPTIONS.TAG,
        label: <FormattedLabel id={GROUP_OPTIONS.TAG} defaultMessage="Tag" />,
        onClick: () => setGroupValue(GROUP_OPTIONS.TAG),
      },
      {
        key: GROUP_OPTIONS.COMPLETION,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.COMPLETION}
            defaultMessage="Completion"
          />
        ),
        onClick: () => setGroupValue(GROUP_OPTIONS.COMPLETION),
      },
      {
        key: GROUP_OPTIONS.PRIORITY,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.PRIORITY}
            defaultMessage="Priority"
          />
        ),
        onClick: () => setGroupValue(GROUP_OPTIONS.PRIORITY),
      },
      {
        key: GROUP_OPTIONS.PROJECT,
        label: (
          <FormattedLabel id={GROUP_OPTIONS.PROJECT} defaultMessage="Project" />
        ),
        onClick: () => setGroupValue(GROUP_OPTIONS.PROJECT),
      },
    ],
    [createCustomFieldItems],
  );

  const xAxisItems = useMemo(
    () => [
      {
        key: GROUP_OPTIONS.ASSIGNEE,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.ASSIGNEE}
            defaultMessage="Assignee"
          />
        ),
        onClick: () => setXAxisValue(GROUP_OPTIONS.ASSIGNEE),
      },
      {
        key: GROUP_OPTIONS.CREATOR,
        label: (
          <FormattedLabel id={GROUP_OPTIONS.CREATOR} defaultMessage="Creator" />
        ),
        onClick: () => setXAxisValue(GROUP_OPTIONS.CREATOR),
      },
      {
        key: GROUP_OPTIONS.CUSTOM_FIELD,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.CUSTOM_FIELD}
            defaultMessage="Custom Field"
          />
        ),
        children: createCustomFieldItems((cf) =>
          setXAxisValue(`${GROUP_OPTIONS.CUSTOM_FIELD_PREFIX}${cf?._id}`),
        ),
      },
      {
        key: GROUP_OPTIONS.TAG,
        label: <FormattedLabel id={GROUP_OPTIONS.TAG} defaultMessage="Tag" />,
        onClick: () => setXAxisValue(GROUP_OPTIONS.TAG),
      },
      {
        key: GROUP_OPTIONS.COMPLETION,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.COMPLETION}
            defaultMessage="Completion"
          />
        ),
        onClick: () => setXAxisValue(GROUP_OPTIONS.COMPLETION),
      },
      {
        key: GROUP_OPTIONS.PRIORITY,
        label: (
          <FormattedLabel
            id={GROUP_OPTIONS.PRIORITY}
            defaultMessage="Priority"
          />
        ),
        onClick: () => setXAxisValue(GROUP_OPTIONS.PRIORITY),
      },
      {
        key: GROUP_OPTIONS.PROJECT,
        label: (
          <FormattedLabel id={GROUP_OPTIONS.PROJECT} defaultMessage="Project" />
        ),
        onClick: () => setXAxisValue(GROUP_OPTIONS.PROJECT),
      },
    ],
    [createCustomFieldItems],
  );

  const measureItems = useMemo(
    () => [
      {
        key: REPORTS.MEASURES.TASK_COUNT,
        label: (
          <FormattedLabel
            id={REPORTS.MEASURES.TASK_COUNT}
            defaultMessage="Tasks (count in numbers)"
          />
        ),
        onClick: () => setMeasure(REPORTS.MEASURES.TASK_COUNT),
      },
    ],
    [],
  );

  const formattedGroupValue = useMemo(
    () => getGroupLabel(groupValue?.replace('-', ' '), workspaceCustomFields),
    [groupValue, workspaceCustomFields],
  );

  const formattedXAxisValue = useMemo(
    () => getGroupLabel(xAxisValue?.replace('-', ' '), workspaceCustomFields),
    [workspaceCustomFields, xAxisValue],
  );

  const showGroupDropdown = useMemo(
    () =>
      [
        CHART_TYPES.GROUPED_BAR,
        CHART_TYPES.STACKED_BAR,
        CHART_TYPES.PIE,
      ].includes(selectedChart),
    [selectedChart],
  );

  const handleFilterChange = useCallback((filters) => {
    const transformedFilters = Object.keys(filters).reduce(
      (result, currentFilterKey) => {
        if (isEmpty(filters?.[currentFilterKey])) return result;

        if (currentFilterKey === 'assignedTo') {
          return {
            ...result,
            assignedTo:
              filters?.assignedTo?.map((item) => item)?.join(',') || '',
          };
        }

        if (
          !['assignedTo', 'priority', 'status', 'dueDate', 'tags'].includes(
            currentFilterKey,
          )
        ) {
          return {
            ...result,
            [`customField[${currentFilterKey}]`]: filters?.[currentFilterKey],
          };
        }

        return {
          ...result,
          [currentFilterKey]: filters?.[currentFilterKey],
        };
      },
      {},
    );

    setFilters(transformedFilters);
  }, []);

  const handlePortfolioProjectsChange = useCallback(
    (selectedIds) => {
      const selectedSource =
        includeFrom === INCLUDE_FROM_PROJECTS ? projects : workspacePortfolios;
      const selected = selectedSource.filter((p) =>
        selectedIds.includes(p._id),
      );
      setPortfolioProjects(selected);
    },
    [includeFrom, projects, workspacePortfolios],
  );

  const getComputedTitle = useCallback(() => {
    if (isBurnChart) {
      return selectedChart === CHART_TYPES.BURNUP
        ? 'Tasks completion over time'
        : 'Remaining tasks over time';
    }
    if (isStackedOrGrouped && groupValue) {
      const groupByLabel = getGroupLabel(
        currentGroupValue,
        workspaceCustomFields,
      );
      const stackByLabel = getGroupLabel(groupValue, workspaceCustomFields);
      return `Total tasks by ${groupByLabel} and ${stackByLabel}`;
    }
    if (selectedChart === CHART_TYPES.NUMBER) {
      return 'Total tasks';
    }
    return `Total tasks by ${getGroupLabel(currentGroupValue, workspaceCustomFields)}`;
  }, [
    currentGroupValue,
    groupValue,
    isBurnChart,
    isStackedOrGrouped,
    selectedChart,
    workspaceCustomFields,
  ]);

  const buildReportQuery = useCallback(() => {
    let _ids = portfolioProjects?.map((p) => p?._id).join(',');

    const query = {
      ...(Object.keys(filters)?.reduce((result, filterKey) => {
        const filterValue = filters?.[filterKey] || '';
        return {
          ...result,
          [filterKey]: Array.isArray(filterValue)
            ? filterValue?.join(',')
            : filterValue,
        };
      }, {}) || {}),
      reportOn: 'task',
      portfolioIds: includeFrom === INCLUDE_FROM_PROJECTS ? '' : _ids,
      projectIds: includeFrom === INCLUDE_FROM_PROJECTS ? _ids : '',
      creator: filters?.creator?.map((item) => item?._id)?.join(',') || '',
    };

    if (!isBurnChart) {
      query.groupBy = currentGroupValue;
      if (isStackedOrGrouped && groupValue) {
        query.stackBy = groupValue;
      }
    } else {
      query.groupBy = GROUP_OPTIONS.BURN_REPORT;
      query.interval = interval;

      let dateRange;
      if (rangeType === RANGE_TYPE_OPTIONS.LAST) {
        dateRange = `last:${rangeNumber}:${timeUnit}`;
      } else if (rangeType === RANGE_TYPE_OPTIONS.BETWEEN) {
        const start = dayjs(startDate).format('YYYY-MM-DD');
        const end = dayjs(endDate).format('YYYY-MM-DD');
        dateRange = `between:${start},${end}`;
      }

      query[selectedChart === CHART_TYPES.BURNUP ? 'burnup' : 'burndown'] =
        dateRange;
    }

    return query;
  }, [
    currentGroupValue,
    endDate,
    filters,
    groupValue,
    includeFrom,
    interval,
    isBurnChart,
    isStackedOrGrouped,
    portfolioProjects,
    rangeNumber,
    rangeType,
    selectedChart,
    startDate,
    timeUnit,
  ]);

  const createChart = useCallback(
    (dashboardId) => {
      const finalReportTitle = reportTitle?.trim() || getComputedTitle();
      const query = buildReportQuery();

      dispatch(
        createReport(
          intl,
          {
            title: finalReportTitle,
            description: '',
            query,
            type: selectedChart,
            workspace: currentWorkspace?._id,
            dashboard: dashboardId,
            measure,
          },
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      buildReportQuery,
      currentWorkspace?._id,
      getComputedTitle,
      measure,
      reportTitle,
      selectedChart,
    ],
  );

  const updateChart = useCallback(() => {
    const finalReportTitle = reportTitle?.trim() || getComputedTitle();
    const query = buildReportQuery();

    dispatch(
      updateReport(
        intl,
        data?.reportId,
        {
          title: finalReportTitle,
          description: currentReport?.description || '',
          query,
          type: selectedChart,
          workspace: currentWorkspace?._id,
          measure,
        },
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buildReportQuery,
    currentReport,
    currentWorkspace?._id,
    data?.reportId,
    getComputedTitle,
    measure,
    reportTitle,
    selectedChart,
  ]);

  const onConfirmBtnClick = useCallback(async () => {
    if (disabled) return;

    if (isEdit) {
      updateChart();
    } else if (data?.dashboardId) {
      createChart(data?.dashboardId);
    } else {
      await dispatch(
        createDashboard(
          intl,
          {
            title: dashboardTitle,
            description: '',
            workspace: currentWorkspace?._id,
          },
          (res) => {
            createChart(res?._id);
            navigate(`/dashboard/${res?._id}`);
          },
        ),
      );
    }

    closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    closeModal,
    createChart,
    currentWorkspace?._id,
    dashboardTitle,
    data?.dashboardId,
    disabled,
    isEdit,
    updateChart,
  ]);

  useEffect(() => {
    if (isModalOpen && workspacePortfolios?.length === 0) {
      dispatch(getPortfolios());
    }
  }, [dispatch, isModalOpen, workspacePortfolios?.length]);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const sequence = ++previewSequenceRef.current;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      let _ids = portfolioProjects?.map((p) => p?._id);
      _ids = _ids.join(',');

      let query = '';

      if (includeFrom === INCLUDE_FROM_PROJECTS) {
        query = `&projectIds=${_ids}`;
      } else {
        query = `&portfolioIds=${_ids}`;
      }

      const queryFilter = toQueryString(filters) || '';

      if (
        currentWorkspace?._id &&
        includeFrom &&
        portfolioProjects?.length !== 0 &&
        ((isEdit && editableFieldsLoaded && (isEmpty(loadedFilters) || !isEmpty(filters))) || !isEdit)
      ) {
        let nextChartData;
        if (isBurnChart && isBurnChartDataSelected) {
          nextChartData = await dispatch(
            aggregateQuery(
              intl,
              currentWorkspace?._id,
              GROUP_OPTIONS.BURN_REPORT,
              query + queryFilter + burnChartQuery,
              { signal: controller.signal },
            ),
          );
        } else if ((currentGroupValue && measure) || selectedChart === CHART_TYPES.NUMBER) {
          nextChartData = await dispatch(
            aggregateQuery(
              intl,
              currentWorkspace?._id,
              currentGroupValue || GROUP_OPTIONS.COMPLETION,
              query + queryFilter + stackByQuery,
              { signal: controller.signal },
            ),
          );
        }

        if (
          nextChartData !== undefined &&
          !controller.signal.aborted &&
          previewSequenceRef.current === sequence
        ) {
          setChartData(nextChartData);
        }
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    burnChartQuery,
    currentGroupValue,
    currentWorkspace,
    editableFieldsLoaded,
    filters,
    includeFrom,
    isBurnChart,
    isBurnChartDataSelected,
    isEdit,
    isModalOpen,
    loadedFilters,
    measure,
    portfolioProjects,
    stackByQuery,
    selectedChart
  ]);

  return !isModalOpen ? null : (
    <Modal
      centered
      title={intl.formatMessage(
        isEdit
          ? { id: 'EDIT_CHART', defaultMessage: 'Edit Chart' }
          : { id: 'ADD_CHART', defaultMessage: 'Add Chart' },
      )}
      open={isModalOpen}
      onCancel={closeModal}
      styles={MODAL_STYLES}
      style={MODAL_MINWIDTH}
      className="custom-modal"
      footer={
        <ModalFooter
          disabled={disabled}
          isEdit={isEdit}
          onConfirmBtnClick={onConfirmBtnClick}
        />
      }
      width={MODAL_WIDTH}
    >
      <div className="flex align-center justify-between h-[70vh] max-h-[700px] min-w-[920px]">
        <ChartPreview
          chartData={chartData}
          currentGroupValue={currentGroupValue}
          groupValue={groupValue}
          isStackedOrGrouped={isStackedOrGrouped}
          measure={measure}
          selectedChart={selectedChart}
          workspaceCustomFields={workspaceCustomFields}
          reportTitle={reportTitle}
          onReportTitleChange={setReportTitle}
        />
        <div className="w-[2px] h-full bg-gray-200" />
        <div className="custom-chart-modal-details-container">
          <div className="custom-chart-modal-data-source-container">
            <SectionLabel
              messageId="CHART_DATA_SOURCE"
              defaultMessage="Chart data source"
            />
            {!isEdit && !data?.dashboardId && (
              <LabeledInput
                value={dashboardTitle}
                onChange={(e) => setDashboardTitle(e.target.value)}
                labelId="DASHBOARD_NAME"
                labelDefaultMessage="Dashboard name"
              />
            )}
            <LabeledDropdown
              dropdownItems={reportOnItems}
              labelId="REPORT_ON"
              labelDefaultMessage="Report on"
              value={REPORT_ON_KEY}
              readOnly
            />
            <LabeledDropdown
              dropdownItems={includeTasksFromItems}
              labelId="INCLUDE_TASKS_FROM"
              labelDefaultMessage="Include tasks from"
              value={includeFrom || 'Select source'}
            />
            <LabeledSelect
              labelId={includeFrom?.toUpperCase()}
              labelDefaultMessage={includeFrom}
              placeholderId="SEARCH_OR_SELECT_OPTIONS"
              placeholderDefaultMessage="Search or select options"
              onChange={handlePortfolioProjectsChange}
              options={options}
              value={portfolioProjects?.map((p) => p._id)}
              disabled={options?.length === 0}
              multiSelect={true}
            />
          </div>
          <HorizontalSeparator />
          <div className="custom-chart-modal-chart-data-container">
            <SectionLabel
              messageId="CHART_DETAILS"
              defaultMessage="Chart details"
            />
            <LabeledDropdown
              dropdownItems={chartStyleItems}
              labelId="CHART_STYLE"
              labelDefaultMessage="Chart style"
              value={<ChartIcon chartType={selectedChart} />}
            />
            {isBurnChart && (
              <>
                <LabeledDropdown
                  dropdownItems={intervalItems}
                  labelId="INTERVAL"
                  labelDefaultMessage="Interval"
                  value={
                    interval?.charAt(0).toUpperCase() + interval?.slice(1) ||
                    'Select interval'
                  }
                />
                <LabeledDropdown
                  dropdownItems={rangeTypeItems}
                  labelId="RANGE"
                  labelDefaultMessage="Range"
                  value={
                    rangeType === RANGE_TYPE_OPTIONS.LAST
                      ? 'Within the last'
                      : rangeType === RANGE_TYPE_OPTIONS.BETWEEN
                        ? 'Between'
                        : 'Select range'
                  }
                />
                {rangeType === RANGE_TYPE_OPTIONS.LAST && (
                  <div className="space-y-2">
                    <Label
                      labelId="TIME_PERIOD"
                      labelDefaultMessage="Time period"
                    />
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={rangeNumber}
                        onChange={(e) => setRangeNumber(e.target.value)}
                        className="h-[36px] w-[70px] text-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none hover:border-[#69b1ff]"
                        placeholder="Number"
                      />
                      <LabeledDropdown
                        dropdownItems={timeUnitItems}
                        value={
                          timeUnit?.charAt(0).toUpperCase() + timeUnit?.slice(1)
                        }
                      />
                    </div>
                  </div>
                )}
                {rangeType === RANGE_TYPE_OPTIONS.BETWEEN && (
                  <div className="space-y-2">
                    <Label
                      labelId="DATE_RANGE"
                      labelDefaultMessage="Date range"
                    />
                    <div className="space-y-2">
                      <DatePicker
                        value={startDate}
                        onChange={(date) => setStartDate(date)}
                        placeholder="Start date"
                        className="w-full h-[36px]"
                        format="YYYY-MM-DD"
                      />
                      <DatePicker
                        value={endDate}
                        onChange={(date) => setEndDate(date)}
                        placeholder="End date"
                        className="w-full h-[36px]"
                        format="YYYY-MM-DD"
                        disabledDate={(current) =>
                          startDate && current && current < startDate
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {!EXCLUDED_CHART_TYPES_FOR_X_AXIS.includes(selectedChart) && (
              <LabeledDropdown
                dropdownItems={xAxisItems}
                labelId="X_AXIS"
                labelDefaultMessage="X-Axis"
                value={formattedXAxisValue || 'Select X-Axis'}
              />
            )}
            {showGroupDropdown && (
              <LabeledDropdown
                dropdownItems={groupItems}
                labelId="GROUP"
                labelDefaultMessage="Group"
                value={formattedGroupValue || 'Select group'}
              />
            )}
            {selectedChart === CHART_TYPES.PIE && (
              <LabeledDropdown
                dropdownItems={measureItems}
                labelId="MEASURE"
                labelDefaultMessage="Measure"
                value={measure?.replace('-', ' ') || 'Select measure'}
              />
            )}
            {!EXCLUDED_CHART_TYPES_FOR_X_AXIS.includes(selectedChart) && (
              <LabeledDropdown
                dropdownItems={measureItems}
                labelId="Y_AXIS"
                labelDefaultMessage="Y-Axis"
                value={measure?.replace('-', ' ') || 'Select Y-Axis'}
              />
            )}
          </div>
          <HorizontalSeparator />
          <div className="custom-chart-modal-chart-data-container">
            <div className="custom-chart-modal-add-filters space-y-2">
              <Label labelId="FILTERS" labelDefaultMessage="Filters" />
              <FilterDropdown
                key="chart-builder"
                participants={currentWorkspace?.members || []}
                projectId={null}
                isWorkspaceLevel
                onChange={handleFilterChange}
                filtersToLoad={loadedFilters}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const modalData = state.modalReducer.customChartModal;
  const reportId = modalData?.reportId;

  const currentReport = getReportById(state, { reportId });

  const isEdit = !isEmpty(currentReport);

  return {
    currentReport,
    currentWorkspace: state?.workspaceReducer?.currentWorkspace,
    data: modalData,
    isEdit,
    projects: state.projectReducer.myProjects,
    workspaceCustomFields: getWorkspaceCustomFields(state),
    workspacePortfolios: state.workspaceReducer.workspacePortfolios,
  };
};

export default connect(mapStateToProps)(CustomChartModal);
